// Service Worker Debugging Utilities
export const SW_DEBUG = {
  // Enable detailed logging
  enabled: true, // Always enabled to track issues
  verbose: import.meta.env.DEV, // Verbose only in dev

  // Log with timestamp and context
  log: (message: string, data?: any, forceLog = false) => {
    if (!SW_DEBUG.enabled && !forceLog) {
      return;
    }

    // In production, log important messages and periodic checks
    const importantKeywords = [
      "Update found",
      "installed",
      "waiting",
      "error",
      "failed",
      "Checking for service worker updates",
      "Setting up update check interval",
      "registered successfully",
      "update check completed",
    ];
    const isImportant = importantKeywords.some((keyword) =>
      message.toLowerCase().includes(keyword.toLowerCase()),
    );

    if (!SW_DEBUG.verbose && !isImportant && !forceLog) {
      return;
    }

    const timestamp = new Date().toISOString();
    console.log(`[SW ${timestamp}] ${message}`, data || "");
  },

  // Check service worker status
  checkStatus: async () => {
    if (!("serviceWorker" in navigator)) {
      SW_DEBUG.log("Service Worker not supported");
      return null;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      SW_DEBUG.log("No service worker registered");
      return null;
    }

    const status = {
      scope: registration.scope,
      active: registration.active?.state,
      waiting: registration.waiting?.state,
      installing: registration.installing?.state,
      updateFound: false,
    };

    SW_DEBUG.log("Service Worker Status:", status);
    return status;
  },

  // Force update check
  forceUpdate: async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      SW_DEBUG.log("No service worker to update");
      return false;
    }

    SW_DEBUG.log("Forcing service worker update check...");
    try {
      await registration.update();
      SW_DEBUG.log("Update check completed");
      return true;
    } catch (error) {
      SW_DEBUG.log("Update check failed:", error);
      return false;
    }
  },

  // Monitor service worker events
  startMonitoring: async () => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }

    // Check if update monitoring is already set up
    if ((window as any).__swMonitoringActive) {
      SW_DEBUG.log("Service worker monitoring already active");
      return;
    }
    (window as any).__swMonitoringActive = true;

    // Monitor update found
    registration.addEventListener("updatefound", () => {
      SW_DEBUG.log("🎯 Update found! New service worker is installing...");

      // Check current state
      console.log("[SW Monitor] State at update found:", {
        active: registration.active?.state,
        waiting: registration.waiting?.state,
        installing: registration.installing?.state,
        controller: navigator.serviceWorker.controller ? "exists" : "none",
      });

      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          SW_DEBUG.log(`🔄 New worker state: ${newWorker.state}`);

          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            SW_DEBUG.log(
              "✅ New service worker installed and waiting to activate",
            );
            SW_DEBUG.log("💡 User should see update notification now!");

            // Force check the registration for waiting worker
            navigator.serviceWorker.getRegistration().then((reg) => {
              console.log("[SW Monitor] Registration state after install:", {
                active: reg?.active?.state,
                waiting: reg?.waiting?.state,
                installing: reg?.installing?.state,
              });

              if (reg?.waiting) {
                console.log("[SW Monitor] Waiting worker confirmed!");
                // Check if there's a callback to trigger the update notification
                if ((window as any).__swUpdateCallback) {
                  console.log(
                    "[SW] Manually triggering update notification from monitor",
                  );
                  (window as any).__swUpdateCallback();
                }
              } else {
                console.log(
                  "[SW Monitor] No waiting worker found after install - service worker might have auto-activated",
                );
              }
            });
          }
        });
      }
    });

    // Monitor controller change
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      SW_DEBUG.log("🚀 Controller changed! New service worker is now active");
    });

    // Monitor messages from service worker
    navigator.serviceWorker.addEventListener("message", (event) => {
      SW_DEBUG.log("📩 Message from service worker:", event.data);
    });

    // Log current state
    SW_DEBUG.log("Service worker monitoring started");
    SW_DEBUG.log("Current registration state:", {
      active: registration.active?.state,
      waiting: registration.waiting?.state,
      installing: registration.installing?.state,
    });
  },

  // Get detailed info about cached resources
  getCacheInfo: async () => {
    if (!("caches" in window)) {
      SW_DEBUG.log("Cache API not supported");
      return null;
    }

    try {
      const cacheNames = await caches.keys();
      const cacheInfo: Record<string, string[]> = {};

      for (const name of cacheNames) {
        const cache = await caches.open(name);
        const requests = await cache.keys();
        cacheInfo[name] = requests.map((r) => r.url);
      }

      SW_DEBUG.log("Cache info:", cacheInfo);
      return cacheInfo;
    } catch (error) {
      SW_DEBUG.log("Failed to get cache info:", error);
      return null;
    }
  },

  // Clear all caches (useful for testing updates)
  clearAllCaches: async () => {
    if (!("caches" in window)) {
      return false;
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      SW_DEBUG.log("All caches cleared");
      return true;
    } catch (error) {
      SW_DEBUG.log("Failed to clear caches:", error);
      return false;
    }
  },

  // Simulate an update by modifying the service worker
  simulateUpdate: () => {
    SW_DEBUG.log("To simulate an update:");
    SW_DEBUG.log("1. Make a small change to any file");
    SW_DEBUG.log("2. The dev server will rebuild");
    SW_DEBUG.log("3. Call window.swDebug.forceUpdate()");
    SW_DEBUG.log("4. You should see update notifications");
  },
};

// Expose debugging utilities globally (useful for production debugging too)
(window as any).swDebug = SW_DEBUG;
if (import.meta.env.DEV) {
  SW_DEBUG.log(
    "Service Worker debugging utilities loaded. Access via window.swDebug",
  );
}
